import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import AccessDenied from '../Auth/AccessDenied';
import { useAuth } from '../../contexts/AuthContext';
import { useRouteAccess } from '../../hooks/useRouteAccess';
import { useSubscriptionAccess } from '../../hooks/useSubscriptionAccess';
import { useTranslation } from 'react-i18next';
import { getAppHomePath } from '../../constants/navigation';
import { isRequesterRole } from '../../utils/tenantProfile';
import BillingStatusBanner from './BillingStatusBanner';
import LinaDock from '../lina/LinaDock';
import ProductTour from '../../tutorials/ProductTour';
import { useTenant } from '../../contexts/TenantContext';

const Layout = () => {
  const { t } = useTranslation('common');
  const { pathname } = useLocation();
  const { user } = useAuth();
  const { tenant } = useTenant();
  const { canAccessRoute, isRoutePending } = useRouteAccess();
  const { access } = useSubscriptionAccess();
  const pending = isRoutePending(pathname);
  const allowed = !pending && canAccessRoute(pathname);
  const assistantBleed = allowed && (pathname === '/assistant' || pathname.startsWith('/assistant/'));
  const requesterHome = isRequesterRole(user) ? getAppHomePath(user) : null;
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    const open = () => setMenuOpen(true);
    const close = () => setMenuOpen(false);
    window.addEventListener('ambiental:open-menu', open);
    window.addEventListener('ambiental:close-menu', close);
    return () => {
      window.removeEventListener('ambiental:open-menu', open);
      window.removeEventListener('ambiental:close-menu', close);
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  return (
    <div className="flex h-dvh min-w-0 overflow-hidden bg-gray-50">
      {menuOpen ? (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          aria-label="Fechar menu"
          onClick={() => setMenuOpen(false)}
        />
      ) : null}
      <Sidebar open={menuOpen} onClose={() => setMenuOpen(false)} />

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <Header onMenu={() => setMenuOpen(true)} />
        <BillingStatusBanner access={access} />

        <main
          className={
            assistantBleed
              ? 'flex min-h-0 flex-1 flex-col overflow-hidden p-3 sm:p-4 lg:p-6'
              : 'flex-1 overflow-y-auto overflow-x-hidden p-3 sm:p-4 lg:p-6'
          }
        >
          <div
            className={
              assistantBleed
                ? 'mx-auto flex min-h-0 w-full max-w-content flex-1 flex-col'
                : 'mx-auto w-full max-w-content'
            }
          >
            {pending ? (
              <p className="text-gray-600">{t('loading')}</p>
            ) : allowed ? (
              <Outlet />
            ) : requesterHome && pathname === '/' ? (
              <Navigate to={requesterHome} replace />
            ) : (
              <AccessDenied />
            )}
          </div>
        </main>
      </div>
      <LinaDock />
      <ProductTour user={user} tenant={tenant} enabled={Boolean(user) && allowed} />
    </div>
  );
};

export default Layout;
